import { createContext, useContext, useState, useCallback, useRef } from "react"
import { SHORT_CONFIG } from "constants/short"

const ShortContext = createContext(null)

export function ShortProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(true)
  const [showControls, setShowControls] = useState(true)
  const [muted, setMuted] = useState(true)
  const [volume, setVolume] = useState(SHORT_CONFIG.MIN_VOLUME)
  const [prevVolume, setPrevVolume] = useState(SHORT_CONFIG.DEFAULT_VOLUME)
  const [userInteracted, setUserInteracted] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  const videoRefs = useRef([])
  const hideControlsTimer = useRef(null)

  const togglePlay = useCallback(() => {
    setIsPlaying((prev) => !prev)
  }, [])

  const handleUserInteraction = useCallback(() => {
    setShowControls(true)
    if (hideControlsTimer.current) clearTimeout(hideControlsTimer.current)
    hideControlsTimer.current = setTimeout(() => {
      setShowControls(false)
    }, SHORT_CONFIG.CONTROLS_HIDE_DELAY)
  }, [])

  const handleVolumeChange = useCallback(
    (e) => {
      const v = Number(e.target.value)

      if (!userInteracted) setUserInteracted(true)

      const safe = Math.min(Math.max(v, SHORT_CONFIG.MIN_VOLUME), SHORT_CONFIG.MAX_VOLUME)
      setVolume(safe)

      if (safe > SHORT_CONFIG.MIN_VOLUME) {
        setMuted(false)
      }
    },
    [userInteracted]
  )

  const handleToggleMute = useCallback(() => {
    const newMuted = !muted

    if (!userInteracted) setUserInteracted(true)

    setMuted(newMuted)

    if (newMuted) {
      setPrevVolume(volume)
      setVolume(SHORT_CONFIG.MIN_VOLUME)
    } else {
      const restored = prevVolume > SHORT_CONFIG.MIN_VOLUME ? prevVolume : SHORT_CONFIG.DEFAULT_VOLUME
      setVolume(restored)
    }
  }, [muted, userInteracted, volume, prevVolume])

  // Update video properties when switching
  const syncVideoState = useCallback(
    (index) => {
      const video = videoRefs.current[index]
      if (!video) return

      if (!userInteracted) {
        video.muted = true
        video.volume = SHORT_CONFIG.MIN_VOLUME
      } else {
        video.muted = muted
        video.volume = volume
      }
    },
    [userInteracted, muted, volume]
  )

  const value = {
    // State
    isPlaying,
    showControls,
    muted,
    volume,
    prevVolume,
    userInteracted,
    showAll,
    currentIndex,
    videoRefs,

    // Setters
    setIsPlaying,
    setShowControls,
    setMuted,
    setVolume,
    setPrevVolume,
    setUserInteracted,
    setShowAll,
    setCurrentIndex,

    // Actions
    togglePlay,
    handleUserInteraction,
    handleVolumeChange,
    handleToggleMute,
    syncVideoState
  }

  return <ShortContext.Provider value={value}>{children}</ShortContext.Provider>
}

export function useShortContext() {
  const context = useContext(ShortContext)
  if (!context) {
    throw new Error("useShortContext must be used within ShortProvider")
  }
  return context
}
